import { View, Text } from "../../components/shared/Themed";
import {
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Platform,
} from "react-native";
import { useEffect, useRef, useState } from "react";
import * as Okta from "../../services/authService";
import { ReadOnlyProps } from "../../utils/templates";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import { ConfigurationDispatcher } from "../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../redux/constants/actionTypesConfigurations";
import { applyLoggedInUser } from "../../navigation/utils/initUser";

interface Props {
  setDisplayForgetPasswordScreen: (e: boolean) => void;
  setDisplayLoginWithTendaScreen: (e: boolean) => void;
}

export const LoginWithTandaScreen = ({
  setDisplayForgetPasswordScreen,
  setDisplayLoginWithTendaScreen,
}: ReadOnlyProps<Props>) => {
  const navigation = useNavigation();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [userPasscode, setUserPasscode] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [errorValidationMessage, setErrorValidationMessage] =
    useState<string>("");

  // Keep a ref so the keyboard listener always sees the latest passcode
  const passcodeRef = useRef(userPasscode);
  useEffect(() => {
    passcodeRef.current = userPasscode;
  }, [userPasscode]);

  // Inject CSS to override browser autofill styling (web only)
  useEffect(() => {
    if (Platform.OS === "web") {
      const style = document.createElement("style");
      style.textContent = `
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active,
        textarea:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 30px rgba(255,255,255,0.5) inset !important;
          box-shadow: 0 0 0 30px rgba(255,255,255,0.5) inset !important;
          -webkit-text-fill-color: #1a1a2e !important;
          background-color: rgba(255,255,255,0.5) !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        input, input:active, input:hover,
        textarea, textarea:active {
          outline: none !important;
          -webkit-appearance: none;
        }
        input:focus, textarea:focus {
          outline: none !important;
          border: 1.5px solid rgba(255,255,255,0.9) !important;
          border-radius: 10px !important;
          box-shadow: 0 0 8px rgba(97,184,255,0.3) !important;
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    }
  }, []);

  // Allow keyboard number input on web only (digits 0-9 and Backspace)
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9" && passcodeRef.current.length < 4) {
        setUserPasscode((prev) => prev + e.key);
      } else if (e.key === "Backspace" && passcodeRef.current.length > 0) {
        setUserPasscode((prev) => prev.slice(0, -1));
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const setPasscode = (passcode: string) => {
    setUserPasscode(userPasscode + passcode);
  };

  useEffect(() => {
    if (userPasscode.length === -1) {
      setErrorValidationMessage("This is required.");
    } else if (userPasscode.length <= 3 && userPasscode.length > 0) {
      setErrorValidationMessage("4-digit passcode is required.");
    } else if (status === "FAILED") {
      setErrorValidationMessage("Invalid passcode");
    } else {
      setErrorValidationMessage("");
    }
  }, [userPasscode, status]);

  const onSubmit = () => {
    setLoading(true);
    if (userPasscode.length === 4) {
      Okta.getAuth(`${userPasscode}@multisteps.com.au`, userPasscode).then(
        (data: any) => {
          setStatus(data.status);
          setLoading(false);
          //Reset the form
          if (data.status !== "FAILED") {
            setUserPasscode("");

            ConfigurationDispatcher(
              actionTypesConfigurations.IsUserAuthorized,
              true,
            );
            applyLoggedInUser(data.user, (logo: string) =>
              ConfigurationDispatcher(actionTypesConfigurations.Logo, logo),
            );

            navigation.reset({
              index: 0,
              routes: [{ name: "Root" }],
            });
          }
        },
      );
    }
  };

  const NUM_ROWS = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
  ];

  return (
    <View style={styles.container}>
      {/* Heading */}
      <Text style={[styles.heading, { backgroundColor: "transparent" }]}>
        Login with TANDA
      </Text>

      {/* Passcode display */}
      <View
        style={[styles.passcodeDotsRow, { backgroundColor: "transparent" }]}
      >
        {[0, 1, 2, 3].map((i) => (
          <View
            key={i}
            style={[
              styles.passcodeDot,
              i < userPasscode.length && styles.passcodeDotFilled,
            ]}
          />
        ))}
      </View>

      {/* Error message */}
      {errorValidationMessage !== "" && (
        <Text style={styles.errorText}>{errorValidationMessage}</Text>
      )}

      {/* Number pad */}
      <View
        style={[styles.numpadContainer, { backgroundColor: "transparent" }]}
      >
        {NUM_ROWS.map((row, rowIdx) => (
          <View
            key={rowIdx}
            style={[styles.numpadRow, { backgroundColor: "transparent" }]}
          >
            {row.map((num) => (
              <TouchableOpacity
                key={num}
                style={[
                  styles.numpadButton,
                  userPasscode.length > 3 && styles.numpadButtonDisabled,
                ]}
                onPress={() => setPasscode(num)}
                disabled={userPasscode.length > 3}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.numpadButtonText,
                    { backgroundColor: "transparent" },
                    userPasscode.length > 3 && styles.numpadButtonTextDisabled,
                  ]}
                >
                  {num}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        ))}
        {/* Zero row - full width */}
        <View style={[styles.numpadRow, { backgroundColor: "transparent" }]}>
          <TouchableOpacity
            style={[
              styles.numpadButton,
              styles.numpadButtonZero,
              userPasscode.length > 3 && styles.numpadButtonDisabled,
            ]}
            onPress={() => setPasscode("0")}
            disabled={userPasscode.length > 3}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.numpadButtonText,
                { backgroundColor: "transparent" },
                userPasscode.length > 3 && styles.numpadButtonTextDisabled,
              ]}
            >
              0
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Divider */}
      <View style={[styles.dividerRow, { backgroundColor: "transparent" }]}>
        <View style={styles.dividerLine} />
      </View>

      {/* Clear Button */}
      <TouchableOpacity
        style={[
          styles.clearButton,
          userPasscode.length === 0 && styles.clearButtonDisabled,
        ]}
        onPress={() => {
          const clearPasscode = userPasscode.substring(
            0,
            userPasscode.length - 1,
          );
          setUserPasscode(clearPasscode);
        }}
        disabled={userPasscode.length === 0}
        activeOpacity={0.7}
      >
        <Feather
          name="delete"
          size={16}
          color={userPasscode.length === 0 ? "#bbb" : "#1a1a2e"}
          style={{ marginRight: 6 }}
        />
        <Text
          style={[
            styles.clearButtonText,
            { backgroundColor: "transparent" },
            userPasscode.length === 0 && styles.clearButtonTextDisabled,
          ]}
        >
          Clear
        </Text>
      </TouchableOpacity>

      {/* Login Button */}
      <TouchableOpacity
        style={[
          styles.loginButton,
          userPasscode.length !== 4 && styles.loginButtonDisabled,
        ]}
        onPress={() => onSubmit()}
        disabled={userPasscode.length !== 4}
        activeOpacity={0.8}
      >
        <Text
          style={[styles.loginButtonText, { backgroundColor: "transparent" }]}
        >
          {isLoading ? "Logging in..." : "Login"}
        </Text>
      </TouchableOpacity>

      {/* Back to Login */}
      <TouchableOpacity
        onPress={() => {
          setDisplayForgetPasswordScreen(false);
          setDisplayLoginWithTendaScreen(false);
        }}
        style={styles.backToLoginWrapper}
      >
        <Text
          style={[styles.backToLoginText, { backgroundColor: "transparent" }]}
        >
          Back to Login
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: 10,
    paddingBottom: 25,
    backgroundColor: "transparent",
  },
  heading: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#1a1a2e",
    textAlign: "center",
    marginBottom: 24,
  },
  passcodeDotsRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 24,
    gap: 16,
  },
  passcodeDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "rgba(180,200,220,0.8)",
    backgroundColor: "rgba(255,255,255,0.5)",
  },
  passcodeDotFilled: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 12,
    textAlign: "center",
    marginTop: -16,
    marginBottom: 16,
  },
  numpadContainer: {
    alignItems: "center",
    marginBottom: 8,
  },
  numpadRow: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 8,
    gap: 8,
  },
  numpadButton: {
    width: 72,
    height: 48,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.75)",
    borderWidth: 1,
    borderColor: "rgba(180,200,220,0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  numpadButtonZero: {
    width: 232,
  },
  numpadButtonDisabled: {
    opacity: 0.4,
  },
  numpadButtonText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#1a1a2e",
  },
  numpadButtonTextDisabled: {
    color: "#aaa",
  },
  dividerRow: {
    marginVertical: 16,
  },
  dividerLine: {
    height: 1,
    backgroundColor: "rgba(180,200,220,0.4)",
  },
  clearButton: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "rgba(180,200,220,0.6)",
    borderRadius: 10,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
    marginBottom: 10,
  },
  clearButtonDisabled: {
    opacity: 0.5,
  },
  clearButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#1a1a2e",
  },
  clearButtonTextDisabled: {
    color: "#bbb",
  },
  loginButton: {
    backgroundColor: "#61B8FF",
    borderRadius: 10,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
  },
  loginButtonDisabled: {
    opacity: 0.45,
  },
  loginButtonText: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  backToLoginWrapper: {
    alignItems: "center",
    marginTop: 20,
  },
  backToLoginText: {
    fontSize: 13,
    color: "#0066CC",
    fontWeight: "600",
  },
});
